<template>
  <div>
    <div class="user-toast">
      <SuccessAlert ref="successAlert" :message="successMessage" :duration="4000" />
    </div>

    <Header name="USUÁRIOS" />

    <p v-if="loading" class="container loading-users">Carregando usuários...</p>

    <div class="container" v-if="!loading">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/home-page">Home</router-link>
        </li>
        <li class="breadcrumb-item active">Usuários</li>
      </ol>

      <p v-if="pageError" class="form-error">{{ pageError }}</p>

      <div ref="userPanel" class="conteudo user-panel">
        <h3>{{ original ? 'Editar usuário' : 'Novo usuário' }}</h3>
        <form class="user-form" @submit.prevent="submit">
          <fieldset class="form-section">
            <legend class="form-section-title">Identificação</legend>
            <div class="form-row form-row--2">
              <label>
                Nome
                <input v-model.trim="form.first_name" class="form-control" type="text" required />
              </label>
              <label>
                Sobrenome
                <input v-model.trim="form.last_name" class="form-control" type="text" required />
              </label>
            </div>
            <div class="form-row form-row--2">
              <label>
                E-mail
                <input v-model.trim="form.email" class="form-control" type="email" required />
              </label>
              <label>
                Usuário
                <input
                  v-if="!original"
                  v-model.trim="form.username"
                  class="form-control"
                  type="text"
                  autocomplete="off"
                  required
                />
                <input v-else class="form-control form-control-readonly" type="text" :value="form.username" readonly />
              </label>
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend class="form-section-title">Senha de acesso</legend>
            <p v-if="original" class="form-hint">Deixe em branco para manter a senha atual.</p>
            <div class="form-row form-row--2">
              <label>
                Senha
                <input
                  v-model="form.password"
                  class="form-control"
                  type="password"
                  autocomplete="new-password"
                  :required="!original"
                />
              </label>
              <label>
                Confirmar senha
                <input
                  v-model="form.password_confirm"
                  class="form-control"
                  type="password"
                  autocomplete="new-password"
                  :required="!original"
                />
              </label>
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend class="form-section-title">Permissões</legend>
            <div class="form-row" :class="form.role === 'farm_admin' ? 'form-row--2' : 'form-row--1'">
              <label>
                Papel
                <select v-model="form.role" class="form-control" @change="onRoleChange">
                  <option v-for="role in roles" :key="role.value" :value="role.value">
                    {{ role.label }}
                  </option>
                </select>
              </label>
              <label v-if="form.role === 'farm_admin'">
                Granja
                <select v-model="form.associated_farm" class="form-control" required>
                  <option value="">Selecione</option>
                  <option v-for="farm in farms" :key="farm.pk" :value="String(farm.pk)">
                    {{ farm.name }}
                  </option>
                </select>
              </label>
            </div>
          </fieldset>

          <div class="form-actions">
            <button class="primary-btn" type="submit" :disabled="saving">
              {{ original ? 'Salvar alterações' : 'Criar usuário' }}
            </button>
            <button v-if="original" class="secondary-btn" type="button" @click="resetForm">
              Cancelar
            </button>
          </div>
        </form>
        <p v-if="formError" class="form-error">{{ formError }}</p>
      </div>

      <div class="conteudo">
        <table class="rTable4">
          <thead>
            <tr>
              <th>USUÁRIO</th>
              <th>NOME</th>
              <th>E-MAIL</th>
              <th>PAPEL</th>
              <th>GRANJA</th>
              <th>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="users.length === 0">
              <td colspan="6">Nenhum usuário encontrado.</td>
            </tr>
            <tr v-for="user in users" :key="user.pk">
              <td>
                <p class="info-galpoes-mobile">Usuário:</p>
                {{ user.username }}
              </td>
              <td>
                <p class="info-galpoes-mobile">Nome:</p>
                {{ user.first_name }} {{ user.last_name }}
              </td>
              <td>
                <p class="info-galpoes-mobile">E-mail:</p>
                {{ user.email }}
              </td>
              <td>
                <p class="info-galpoes-mobile">Papel:</p>
                {{ roleLabel(user.role) }}
              </td>
              <td>
                <p class="info-galpoes-mobile">Granja:</p>
                {{ farmLabel(allFarms, user.associated_farm) }}
              </td>
              <td class="botoes">
                <button type="button" class="btn btn-success btn-circle" title="Editar" @click="edit(user)">
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  v-if="!isSelf(user)"
                  type="button"
                  class="btn btn-success btn-circle"
                  title="Apagar"
                  @click="remove(user)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import SuccessAlert from '../components/SuccessAlert.vue'
import {
  COMPANY_USER_ROLES,
  buildCreatePayload,
  buildPatchPayload,
  deleteCompanyUser,
  emptyCompanyUserForm,
  farmLabel,
  farmOptions,
  formFromUser,
  formatApiError,
  listCompanyUsers,
  roleLabel,
  updateCompanyUser,
  createCompanyUser,
  validateCompanyUserForm,
  canManageCompanyUsers,
} from '../utils/companyUsers'

export default {
  name: 'ListUsersPage',
  components: { Header, SuccessAlert },
  beforeRouteEnter(to, from, next) {
    to.meta.title = 'PecSmart - Usuários'
    document.title = to.meta.title
    next()
  },
  data() {
    return {
      roles: COMPANY_USER_ROLES,
      users: [],
      form: emptyCompanyUserForm(),
      original: null,
      loading: true,
      saving: false,
      formError: '',
      pageError: '',
      successMessage: '',
    }
  },
  computed: {
    allFarms() {
      return this.$store.getters.getAllFarms
    },
    farms() {
      return farmOptions(this.allFarms)
    },
  },
  async created() {
    if (this.$store.getters.getLogedIn === false) {
      this.$router.push(window.localStorage.getItem('Token') ? '/home-page' : '/')
      return
    }
    if (!canManageCompanyUsers(
      this.$store.getters.getCurrentUser,
      this.$store.getters.getCompanySettings,
    )) {
      this.$router.push('/home-page')
      return
    }
    const ready = await this.loadUsers()
    if (ready) this.loading = false
  },
  methods: {
    roleLabel,
    farmLabel,
    isSelf(user) {
      return user.username === window.localStorage.getItem('username')
    },
    onRoleChange() {
      if (this.form.role === 'company_admin') this.form.associated_farm = ''
    },
    resetForm() {
      this.form = emptyCompanyUserForm()
      this.original = null
      this.formError = ''
    },
    showSuccess(message) {
      this.successMessage = message
      this.$nextTick(() => {
        if (this.$refs.successAlert) this.$refs.successAlert.alert()
      })
    },
    scrollToUserForm() {
      const panel = this.$refs.userPanel
      if (!panel) return
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    edit(user) {
      this.original = user
      this.form = formFromUser(user)
      this.formError = ''
      this.pageError = ''
      this.$nextTick(() => this.scrollToUserForm())
    },
    denyAccess() {
      this.$router.push('/home-page')
    },
    applyStatus(result, onForm) {
      if (result.status === 401) return
      if (result.status === 403) {
        this.denyAccess()
        return
      }
      if (result.status === 404) {
        this.pageError = 'Usuário não encontrado'
        this.loadUsers()
        return
      }
      const message = formatApiError(result.body)
      if (onForm) this.formError = message
      else this.pageError = message
    },
    async loadUsers() {
      const result = await listCompanyUsers(this.$store.getters.getHeader)
      if (!result.ok) {
        if (result.status === 404) {
          this.users = []
          return true
        }
        this.applyStatus(result, false)
        return result.status !== 401 && result.status !== 403
      }
      this.users = result.users.slice().sort((a, b) => {
        return String(a.username || '').localeCompare(String(b.username || ''))
      })
      return true
    },
    async submit() {
      this.formError = ''
      this.pageError = ''
      const editing = Boolean(this.original)
      const invalid = validateCompanyUserForm(this.form, { editing })
      if (invalid) {
        this.formError = invalid
        return
      }
      const payload = editing
        ? buildPatchPayload(this.form, this.original)
        : buildCreatePayload(this.form)
      if (editing && Object.keys(payload).length === 0) {
        this.resetForm()
        return
      }
      this.saving = true
      const headers = this.$store.getters.getHeader
      const result = editing
        ? await updateCompanyUser(headers, this.original.pk, payload)
        : await createCompanyUser(headers, payload)
      this.saving = false
      if (result.ok && result.user) {
        const username = result.user.username || this.form.username
        if (editing) {
          this.users = this.users.map((user) => user.pk === result.user.pk ? result.user : user)
        } else {
          this.users = this.users.concat(result.user).sort((a, b) => {
            return String(a.username || '').localeCompare(String(b.username || ''))
          })
        }
        this.resetForm()
        this.showSuccess(editing ? `Usuário ${username} atualizado` : `Usuário ${username} criado`)
        return
      }
      this.applyStatus(result, true)
    },
    async remove(user) {
      if (!window.confirm(`Apagar o usuário ${user.username}?`)) return
      this.pageError = ''
      const result = await deleteCompanyUser(this.$store.getters.getHeader, user.pk)
      if (result.ok) {
        this.users = this.users.filter((item) => item.pk !== user.pk)
        if (this.original && this.original.pk === user.pk) this.resetForm()
        this.showSuccess(`Usuário ${user.username} apagado`)
        return
      }
      this.applyStatus(result, false)
    },
  },
}
</script>

<style scoped>
.user-toast {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 30;
  pointer-events: none;
}

.user-toast >>> .alert.showAlert {
  pointer-events: auto;
}

.loading-users {
  padding: 24px 0;
  font-weight: 300;
}

.breadcrumb {
  background: none;
  padding: 20px 0;
  margin-bottom: 0;
  font-weight: 300;
}

.breadcrumb-item a {
  color: #6ba63c;
}

.user-panel {
  scroll-margin-top: 20px;
}

.user-panel h3 {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 20px;
}

.user-form {
  max-width: 920px;
}

.form-section {
  border: none;
  margin: 0 0 20px;
  padding: 0;
  min-width: 0;
}

.form-section-title {
  float: left;
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: #6ba63c;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e6e6e6;
}

.form-hint {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 300;
  color: #777;
}

.form-row {
  display: grid;
  gap: 12px 20px;
  margin-bottom: 12px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row--1 {
  grid-template-columns: 1fr;
  max-width: 360px;
}

.form-row--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.user-form label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #3e3e3e;
  margin-bottom: 0;
}

.form-control-readonly {
  background: #f4f4f4;
  color: #666;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #e6e6e6;
}

.primary-btn,
.secondary-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 2px;
  border: none;
}

.primary-btn {
  background: #78b843;
  color: #fff;
}

.primary-btn:disabled {
  opacity: 0.6;
}

.secondary-btn {
  background: #e9e9e9;
  color: #3e3e3e;
}

.form-error {
  margin: 12px 0 0;
  color: #9b2c2c;
}

.btn {
  border-radius: 0;
}

.btn-success {
  background-color: #a1cd7b;
  border: none;
}

.btn-success:hover {
  background: #1e7e34;
}

.btn-circle {
  width: 30px;
  height: 42px;
  padding: 0 13px;
  font-size: 15px;
  line-height: 0;
  display: table-cell;
}

.rTable4 {
  width: 100%;
  font-weight: 300;
}

.rTable4 thead {
  font-weight: bold;
  color: #3e3e3e;
  font-size: 14px;
}

.rTable4 tbody {
  background: #f4f4f4;
}

.rTable4 tr {
  border-bottom: solid 10px #fff;
}

.rTable4 td {
  padding: 10px;
}

.botoes {
  white-space: nowrap;
}

.botoes .btn + .btn {
  margin-left: 4px;
}

@media screen and (min-width: 993px) {
  .info-galpoes-mobile {
    display: none;
  }
}

@media screen and (max-width: 767px) {
  .form-row--2 {
    grid-template-columns: 1fr;
  }

  .form-row--1 {
    max-width: none;
  }
}

@media screen and (max-width: 991px) {
  .rTable4 thead {
    display: none;
  }

  .rTable4 tbody td {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .botoes {
    flex-direction: row;
  }

  .info-galpoes-mobile {
    display: contents;
    font-weight: 500;
  }
}
</style>
